import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  notification,
  Alert,
  Space,
  Checkbox,
  Divider,
  Row,
  Col
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { createGroup } from '../../api/group';
import { loadState } from '../../stores/store';
import dayjs from 'dayjs';
import termsPDF from '../../TermsAndConditions/SEMI-FINAL Rules and Regulations 2026 - GROUPS.pdf';
import infoPDF from '../../TermsAndConditions/SACOPA Official Contestant - Information Package 2026.pdf';
import contractPDF from '../../TermsAndConditions/SACOPA Nationals - OFFICIAL CONTRACT 2026.pdf';

const { Option } = Select;

const groupTypeLimits = {
  duet: { min: 2, max: 2 },
  trio: { min: 3, max: 3 },
  'small group': { min: 4, max: 9 },
  'large group': { min: 10, max: 19 },
  'production line': { min: 20, max: 50 }
};

const CreateGroupModal = ({ visible, onClose, onGroupCreated, provinces }) => {
  const [form] = Form.useForm();
  const [groupType, setGroupType] = useState('');
  const [minContestants, setMinContestants] = useState(0);
  const [maxContestants, setMaxContestants] = useState(0);
  const [contestantWarning, setContestantWarning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [infoAccepted, setInfoAccepted] = useState(false);
  const [contractAccepted, setContractAccepted] = useState(false);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalPDF, setModalPDF] = useState(null);

  const canSubmit = termsAccepted;

  const handleGroupTypeChange = (value) => {
    setGroupType(value);
    setMinContestants(groupTypeLimits[value]?.min || 0);
    setMaxContestants(groupTypeLimits[value]?.max || 0);
    setContestantWarning(false);

    const members = form.getFieldValue(['group']) || [];
    if (members.length > (groupTypeLimits[value]?.max || 0)) {
      form.setFieldsValue({ group: members.slice(0, groupTypeLimits[value].max) });
    }
  };

  const actuallyCreateGroup = async (payload, membersCount, values) => {
    const data = await createGroup(payload);

    const newGroup = {
      ...data.group,
      group_size: membersCount,
      group_type: values.group_type,
      group: payload.group
    };

    onGroupCreated(newGroup);
    notification.success({ message: 'Group created successfully!' });
    form.resetFields();
    onClose();
  };

  const handleFormSubmit = async (values) => {
    const membersCount = values.group?.length || 0;

    if (!groupType || minContestants === 0 || maxContestants === 0) {
      notification.error({ message: 'Please select a group type.' });
      return;
    }
    if (membersCount < minContestants) {
      setContestantWarning(true);
      notification.error({
        message: `Please enter at least ${minContestants} members for this group type.`
      });
      return;
    }
    if (membersCount > maxContestants) {
      setContestantWarning(true);
      notification.error({
        message: `You cannot enter more than ${maxContestants} group members for this group type.`
      });
      return;
    }
    setContestantWarning(false);

    const state = loadState();
    const payload = {
      ...values,
      created_by: state.user._id,
      group_size: membersCount,
      province: values.audition_province
    };

    try {
      setIsSubmitting(true);
      await actuallyCreateGroup(payload, membersCount, values);
    } catch (error) {
      // Hard fail on ID conflicts, show detailed info
      if (error.status === 409 && error.data && error.data.conflicts) {
        const conflicts = error.data.conflicts || [];

        const conflictContent = (
          <div>
            <p>
              One or more ID numbers you entered are already used by a different contestant in
              another group created by a different user:
            </p>
            <ul>
            {conflicts.map((c, idx) => (
              <li key={idx}>
                ID <strong>{c.id_number}</strong> is used by{' '}
                <strong>{c.existingMemberName || 'another contestant'}</strong>
                {c.group_name ? ` in group "${c.group_name}"` : ''}
                {c.newMemberName ? ` (you entered: ${c.newMemberName})` : ''}
              </li>
            ))}
            </ul>
            <p>
              If this is the same contestant, please make sure the name you enter is exactly the
              same as the existing one and then try again.
            </p>
          </div>
        );

        Modal.error({
          title: 'ID number already in use',
          content: conflictContent
        });
      } else {
        console.error('Error creating group', error);
        notification.error({
          message: error.message || 'Error creating group'
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    form.resetFields();
    setGroupType('');
    setMinContestants(0);
    setMaxContestants(0);
    setContestantWarning(false);
    setTermsAccepted(false);
    setIsModalVisible(false);
  };

  useEffect(() => {
    if (!visible) {
      resetForm();
    }
  }, [visible]);

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      title="Create Group"
      footer={null}
      destroyOnClose
    >
      <Form
        form={form}
        onFinish={handleFormSubmit}
        layout="vertical"
        initialValues={{
          group: []
        }}
      >
        <Form.Item
          name="group_name"
          label="Group Name"
          rules={[{ required: true, message: 'Please input group name!' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="teacher_name"
          label="Teacher Name"
          rules={[{ required: true, message: 'Please input teacher name!' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="teacher_number"
          label="Teacher Phone Number"
          rules={[{ required: true, message: 'Please input teacher phone number!' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="teacher_email"
          label="Teacher Email"
          rules={[
            { type: 'email', message: 'Invalid email' },
            { required: true, message: 'Please input teacher email!' }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="provincial_director_name"
          label="Provincial Director Name"
          rules={[{ required: true, message: 'Please input provincial director name!' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="group_type"
          label="Group Type"
          rules={[{ required: true, message: 'Please select group type!' }]}
        >
          <Select onChange={handleGroupTypeChange} placeholder="Select group type">
            <Option value="duet">Duet - 2 people</Option>
            <Option value="trio">Trio - 3 people</Option>
            <Option value="small group">Small Group - 4-9 people</Option>
            <Option value="large group">Large Group - 10-19 people</Option>
            <Option value="production line">Production Line - 20+ people</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="group_age"
          label="Group Age"
          rules={[{ required: true, message: 'Please select group age!' }]}
        >
          <Select placeholder="Select group age">
            <Option value="0-04">0-04</Option>
            <Option value="05-07">05-07</Option>
            <Option value="08-10">08-10</Option>
            <Option value="11-12">11-12</Option>
            <Option value="13-15">13-15</Option>
            <Option value="16-17">16-17</Option>
            <Option value="18-24">18-24</Option>
            <Option value="25-29">25-29</Option>
            <Option value="30+">30+</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="audition_province"
          label="Semi Final Audition Date"
          rules={[{ required: true, message: 'Please select your audition date!' }]}
        >
          <Select
            placeholder="Select your audition date"
            showSearch
            optionFilterProp="children"
          >
            {provinces &&
              provinces.map((prov) => (
                <Option key={prov._id} value={prov._id}>
                  <div>
                    <strong>{prov.province}</strong>{' '}
                    <span style={{ color: '#555' }}>
                      {prov.location} - {dayjs(prov.date).format('YYYY-MM-DD')}
                    </span>
                  </div>
                </Option>
              ))}
          </Select>
        </Form.Item>

        <Form.List name="group">
          {(fields, { add, remove }) => (
            <>
              <div style={{ marginBottom: 8, fontWeight: 600 }}>Group Members</div>
              {fields.map((field) => (
                <Space
                  key={field.key}
                  align="start"
                  style={{ display: 'flex', marginBottom: 4 }}
                >
                  <Form.Item
                    {...field}
                    name={[field.name, 'name']}
                    fieldKey={[field.fieldKey, 'name']}
                    rules={[{ required: true, message: 'Enter full name' }]}
                  >
                    <Input placeholder="Full Name" />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'id_number']}
                    fieldKey={[field.fieldKey, 'id_number']}
                    rules={[{ required: true, message: 'Enter ID Number' }]}
                  >
                    <Input placeholder="ID or Passport Number" />
                  </Form.Item>
                  {fields.length > minContestants && (
                    <MinusCircleOutlined
                      onClick={() => {
                        remove(field.name);
                        setContestantWarning(false);
                      }}
                    />
                  )}
                </Space>
              ))}
              <Button
                type="dashed"
                onClick={() => {
                  const members = form.getFieldValue(['group']) || [];
                  if (maxContestants > 0 && members.length < maxContestants) {
                    add();
                    setContestantWarning(false);
                  } else {
                    setContestantWarning(true);
                  }
                }}
                block
                icon={<PlusOutlined />}
                disabled={maxContestants > 0 && fields.length >= maxContestants}
                style={{ marginBottom: 10 }}
              >
                Add Member
              </Button>
              <div style={{ margin: '4px 0' }}>
                <span style={{ color: '#888', fontSize: 13 }}>
                  {`You must add ${minContestants}${
                    minContestants !== maxContestants && groupType
                      ? ` to ${maxContestants}`
                      : ''
                  } members.`}
                </span>
              </div>
            </>
          )}
        </Form.List>

        {contestantWarning && (
          <Alert
            message={
              minContestants === maxContestants
                ? `You must enter exactly ${minContestants} group members.`
                : `You must enter between ${minContestants} and ${maxContestants} group members.`
            }
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Divider />

        <Row align="left" style={{ flexDirection: 'column' }}>
          <Col>
            <Checkbox
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              style={{ marginBottom: 8 }}
            >
              I accept the{' '}
              <Button
                type="link"
                onClick={() => {
                  setModalPDF(termsPDF);
                  setIsModalVisible(true);
                }}
                style={{ padding: 0 }}
              >
                semi-finals rules and regulations
              </Button>
            </Checkbox>
          </Col>
        </Row>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
            disabled={!canSubmit}
          >
            Submit
          </Button>
        </Form.Item>
      </Form>

      <Modal
        title="Terms and Conditions"
        open={isModalVisible}
        onOk={() => setIsModalVisible(false)}
        onCancel={() => setIsModalVisible(false)}
        footer={
          <Button key="close" onClick={() => setIsModalVisible(false)}>
            Close
          </Button>
        }
        width={800}
      >
        {modalPDF && (
          <iframe
            src={modalPDF}
            width="100%"
            height="700px"
            style={{ border: 'none' }}
            title="Terms and Conditions PDF"
          />
        )}
        <Divider />
      </Modal>
    </Modal>
  );
};

export default CreateGroupModal;
